import React, { useState } from "react";
import { getTaxClassificationOptions } from "../../../../constants/accountConstants";
import {
  formatSSNInput,
  formatPhoneInput,
  countSSNDigits,
  validatePhoneDigits,
  validateZipDigits,
} from "../../../../utils/validationHelpers";
import { useToast } from "../../../../context/ToastContext";
import MemberSelect from "../../../shared/MemberSelect";
import StateCitySelect from "../../../StateCitySelect";
import { ToggleField } from "../../shared/DataField";

const isMissing = (val) => val === null || val === undefined || val === "";

const BusinessInfoForm = ({ householdData, fieldData, entityId, onSave, onSaveAndExit, saving, nextLabel }) => {
  const { toast } = useToast();
  const account = fieldData || {};
  const existing = account.business_info || {};
  const members = householdData?.members || [];
  const taxOptions = getTaxClassificationOptions(account.type);

  const needName = isMissing(existing.business_name);
  const needSsn = isMissing(existing.ssn);
  const needOperating = existing.is_operating !== true && existing.is_operating !== false;
  const needTax = taxOptions.length > 0 && isMissing(existing.tax_classification);
  const existingControls = Array.isArray(existing.control_persons)
    ? existing.control_persons.filter((c) => c && c.id)
    : [];
  const needControls = existingControls.length === 0;
  const existingAddr = existing.address || {};
  const needAddressLine1 = isMissing(existingAddr.address_line_1);
  const needCity = isMissing(existingAddr.city);
  const needProvince = isMissing(existingAddr.province);
  const needPostalCode = isMissing(existingAddr.postal_code);
  const needCountry = isMissing(existingAddr.country);
  const needPhone = isMissing(existingAddr.phone);
  const needAddress =
    needAddressLine1 || needCity || needProvince || needPostalCode || needCountry || needPhone;

  const [formData, setFormData] = useState({
    business_name: "",
    ssn: "",
    is_operating: "true",
    is_non_profit: existing.is_non_profit === true,
    tax_classification: "",
    control_persons: [],
    address_line_1: existingAddr.address_line_1 || "",
    address_line_2: existingAddr.address_line_2 || "",
    country: existingAddr.country || "USA",
    province: existingAddr.province || "",
    city: existingAddr.city || "",
    postal_code: existingAddr.postal_code || "",
    phone: existingAddr.phone ? formatPhoneInput(String(existingAddr.phone)) : "",
  });

  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));

  const missingCount =
    (needName ? 1 : 0) + (needSsn ? 1 : 0) + (needOperating ? 1 : 0) +
    (needTax ? 1 : 0) + (needControls ? 1 : 0) +
    (needAddressLine1 ? 1 : 0) + (needCity ? 1 : 0) + (needProvince ? 1 : 0) +
    (needPostalCode ? 1 : 0) + (needCountry ? 1 : 0) + (needPhone ? 1 : 0);

  if (missingCount === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">All business fields have been filled.</p>
      </div>
    );
  }

  const buildPayload = () => {
    const biz = { ...existing };
    if (needName) biz.business_name = formData.business_name || null;
    if (needSsn) biz.ssn = formData.ssn || null;
    if (needOperating && formData.is_operating !== "")
      biz.is_operating = formData.is_operating === "true";
    if (needOperating) biz.is_non_profit = formData.is_non_profit === true;
    if (needTax) biz.tax_classification = formData.tax_classification || null;
    if (needControls)
      biz.control_persons = formData.control_persons.filter((c) => c && c.id);
    if (needAddress) {
      biz.address = {
        ...existingAddr,
        address_line_1: formData.address_line_1 || null,
        address_line_2: formData.address_line_2 || existingAddr.address_line_2 || null,
        country: formData.country || null,
        province: formData.province || null,
        city: formData.city || null,
        postal_code: formData.postal_code || null,
        phone: formData.phone.replace(/\D/g, "") || null,
      };
    }
    return { accounts: { [entityId]: { business_info: biz } } };
  };

  const validate = () => {
    if (needName && !formData.business_name.trim()) return "Business Name is required.";
    if (needSsn && countSSNDigits(formData.ssn) !== 9) return "Business Tax ID must be 9 digits.";
    if (needOperating && formData.is_operating === "") return "Please select whether the business is operating.";
    if (needTax && !formData.tax_classification) return "Tax Classification is required.";
    if (needControls && formData.control_persons.filter((c) => c && c.id).length === 0)
      return "At least one control person is required.";
    if (needAddress) {
      if (!formData.address_line_1.trim()) return "Business address line 1 is required.";
      if (!formData.city.trim()) return "Business address city is required.";
      if (!formData.province.trim()) return "Business address state/province is required.";
      if (!validateZipDigits(formData.postal_code)) return "Business address postal code must be 5 digits.";
      if (!formData.country) return "Business address country is required.";
      if (!validatePhoneDigits(formData.phone)) return "Business phone must be 10 digits.";
    }
    return null;
  };

  const handleSubmit = (saveCallback) => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    saveCallback(buildPayload());
  };

  const inputCls =
    "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        <span className="text-sm text-red-500 font-medium">
          ● {missingCount} Required field{missingCount !== 1 ? "s" : ""} missing
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {needName && (
          <div>
            <label className={labelCls}>Business Name <span className="text-red-500">*</span></label>
            <input type="text" value={formData.business_name} onChange={(e) => set("business_name", e.target.value)} className={inputCls} />
          </div>
        )}
        {needSsn && (
          <div>
            <label className={labelCls}>Business Tax ID (EIN) <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={formatSSNInput(formData.ssn)}
              onChange={(e) => set("ssn", e.target.value.replace(/\D/g, "").substring(0, 9))}
              className={inputCls}
              placeholder="XX-XXXXXXX"
            />
          </div>
        )}
        {needOperating && (
          <div>
            <label className={labelCls}>Is the business operating? <span className="text-red-500">*</span></label>
            <select value={formData.is_operating} onChange={(e) => set("is_operating", e.target.value)} className={inputCls}>
              <option value="">Select...</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        )}
        {needOperating && (
          <ToggleField
            label="Is this a non-profit organization?"
            value={formData.is_non_profit === true}
            isEditable
            isEditing
            fieldPath="is_non_profit"
            formData={formData}
            onFieldChange={(fp, v) => set(fp, v)}
            variant="field"
          />
        )}
        {needTax && (
          <div>
            <label className={labelCls}>Tax Classification <span className="text-red-500">*</span></label>
            <select value={formData.tax_classification} onChange={(e) => set("tax_classification", e.target.value)} className={inputCls}>
              <option value="">Select...</option>
              {taxOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        )}
        {needControls && (
          <div className="md:col-span-2">
            <MemberSelect
              multi
              label="Control Persons"
              required
              members={members}
              value={formData.control_persons}
              onChange={(next) => set("control_persons", next)}
              addLabel="+ Add Control Person"
            />
          </div>
        )}
        {needAddress && (
          <>
            <div>
              <label className={labelCls}>Address Line 1 <span className="text-red-500">*</span></label>
              <input type="text" value={formData.address_line_1} onChange={(e) => set("address_line_1", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Address Line 2</label>
              <input type="text" value={formData.address_line_2} onChange={(e) => set("address_line_2", e.target.value)} className={inputCls} />
            </div>
            <div className="md:col-span-2 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
              <StateCitySelect
                countryValue={formData.country}
                stateValue={formData.province}
                cityValue={formData.city}
                onCountryChange={(val) => setFormData((p) => ({ ...p, country: val, province: "", city: "" }))}
                onStateChange={(val) => set("province", val)}
                onCityChange={(val) => set("city", val)}
                isEditing={true}
                stateLabel="State"
                cityLabel="City"
                zipValue={formData.postal_code}
                onZipChange={(val) => set("postal_code", val)}
                zipLabel="Postal Code"
                zipRestrictUsFive
              />
            </div>
            <div>
              <label className={labelCls}>Phone <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => set("phone", formatPhoneInput(e.target.value))}
                className={inputCls}
                placeholder="XXX-XXX-XXXX"
              />
            </div>
          </>
        )}
      </div>

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button onClick={() => handleSubmit(onSaveAndExit)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">
          Save & Exit
        </button>
        <button onClick={() => handleSubmit(onSave)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2">
          {saving && <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

export default BusinessInfoForm;
